package com.evoapps.evoshift.ui.finance

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DayResults
import com.evoapps.evoshift.ui.util.formatPercentOrDash
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.daily_records_margin_plan
import evoshift.composeapp.generated.resources.daily_records_margin_real
import evoshift.composeapp.generated.resources.daily_records_month_summary
import evoshift.composeapp.generated.resources.daily_records_result_plan
import evoshift.composeapp.generated.resources.daily_records_result_real
import org.jetbrains.compose.resources.stringResource

/*
 * SH-195: what the owner earns, per day and for the month so far: the result (income − costs, the
 * monthly result's rule day by day, DayResults) and its margin (the result as a share of the
 * income), beside the labour % and SPMH. Tapping a result shows its revenue and cost lines.
 * Money: only on the daily records, which owners and financial managers see.
 */

/** Positive results green, losses red (as the day cards always showed them). */
internal fun dayResultColor(result: Double): Color = if (result >= 0) Color(0xFF2E7D32) else Color(0xFFD32F2F)

/** The day's result plan | actual and its margin plan | actual as two rows of [CardFigures]. */
@Composable
internal fun DayResultFigures(
    record: DailyRecord,
    currency: String,
    labelColor: Color = MaterialTheme.colorScheme.onSurfaceVariant,
    highlightColor: Color = MaterialTheme.colorScheme.primary,
    /** On the navy cards the actual is highlighted in gold; on light ones it is coloured by its sign. */
    onDark: Boolean = false,
    preview: Boolean = false
) {
    val why = rememberDayWhy(record)
    val hasActuals = record.revenue.realNetto != null
    // The day's share of the month's recurring costs, not the whole rent on the 1st.
    val resultDay = LocalResultDay.current
    val day = remember(record, resultDay) { resultDay(record) }
    CardFigures(
        stringResource(Res.string.daily_records_result_plan) to formatMoney(day.resultPlan, currency),
        stringResource(Res.string.daily_records_result_real) to (if (hasActuals) formatMoney(day.resultReal, currency) else "-"),
        firstWhy = why.result(Side.PLAN), secondWhy = why.result(Side.ACTUAL), currency = currency,
        highlightSecond = onDark && hasActuals,
        resultColor = if (!onDark && hasActuals) dayResultColor(day.resultReal) else null,
        labelColor = labelColor, highlightColor = highlightColor, preview = preview
    )
    CardFigures(
        stringResource(Res.string.daily_records_margin_plan) to formatPercentOrDash(day.resultPercentPlan),
        stringResource(Res.string.daily_records_margin_real) to formatPercentOrDash(day.resultPercentReal),
        firstWhy = why.result(Side.PLAN), secondWhy = why.result(Side.ACTUAL), currency = currency,
        labelColor = labelColor, highlightColor = highlightColor, preview = preview
    )
}

/** Test tag of the phone's month summary above the day cards. */
const val DAILY_MONTH_SUMMARY_TAG = "daily_records_month_summary"

